<section class="users" aria-labelledby="users-title">
  <h1 id="users-title" class="sr-only">Users</h1>

  <!-- Toolbar -->
  <div class="toolbar">
    <label class="search">
      <span class="sr-only">Search users</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7"></circle>
        <path d="m20 20-3.5-3.5"></path>
      </svg>
      <input #search type="search" placeholder="by name, email or employee code" (input)="onSearch(search.value)" />
    </label>

    <div class="select">
      <label class="sr-only" for="page-size">Rows per page</label>
      <select #size id="page-size" (change)="onPageSizeChange(size.value)">
        <option *ngFor="let n of pageSizeOptions" [value]="n" [selected]="n === pageSize">
          {{ n }} per page
        </option>
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="m6 9 6 6 6-6"></path>
      </svg>
    </div>

    <span class="toolbar__spacer"></span>

    <button type="button" class="btn-primary" (click)="onAddUser()">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14"></path>
      </svg>
      Add user
    </button>

    <div class="pagination">
      <span class="pagination__range" aria-live="polite">
        {{ rangeLabel }}
      </span>
      <div class="pagination__buttons">
        <button type="button" class="icon-btn" aria-label="Previous page" [disabled]="!hasPrev" (click)="prevPage()">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m15 6-6 6 6 6"></path>
          </svg>
        </button>
        <button type="button" class="icon-btn" aria-label="Next page" [disabled]="!hasNext" (click)="nextPage()">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m9 6 6 6-6 6"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>

  <!-- Bulk action bar: appears when at least one user is selected -->
  <div class="bulk-bar" *ngIf="selectedCount > 0" role="region" aria-label="Bulk actions">
    <span class="bulk-bar__count">{{ selectedCount }} selected</span>

    <div class="select">
      <label class="sr-only" for="bulk-role">Role to add</label>
      <select #roleSelect id="bulk-role" [disabled]="rolesLoading || assigning"
              (change)="selectedRoleName = roleSelect.value">
        <option value="">{{ rolesLoading ? 'Loading roles…' : 'Select a role' }}</option>
        <option *ngFor="let r of roles" [value]="r.name" [selected]="r.name === selectedRoleName">{{ r.name }}</option>
      </select>
    </div>

    <button type="button" class="btn-primary" (click)="assignRoleToSelected()"
            [disabled]="!selectedRoleName || assigning">
      {{ assigning ? 'Adding…' : 'Add role' }}
    </button>

    <button type="button" class="link-btn" (click)="clearSelection()" [disabled]="assigning">Clear selection</button>

    <span class="bulk-bar__error" *ngIf="rolesError">
      {{ rolesError }} <button type="button" class="link-btn" (click)="loadRoles()">Try again</button>
    </span>
  </div>

  <!-- Table -->
  <div class="table-wrap" [class.is-loading]="loading" [attr.aria-busy]="loading">
    <table>
      <thead>
        <tr>
          <th scope="col" class="col-select">
            <input type="checkbox" aria-label="Select all users on this page"
                   [checked]="allOnPageSelected" [indeterminate]="someOnPageSelected"
                   [disabled]="visibleUsers.length === 0 || assigning"
                   (change)="toggleAllOnPage($any($event.target).checked)" />
          </th>
          <th scope="col" class="col-user">Full Name</th>
          <th scope="col" class="col-username">Username</th>
          <th scope="col" class="col-code">Employee code</th>
          <th scope="col" class="col-status">Status</th>
          <th scope="col" class="col-actions">Action</th>
        </tr>
      </thead>

      <tbody>
        <!-- Loading -->
        <tr *ngIf="loading && visibleUsers.length === 0" class="empty-row">
          <td colspan="6" role="status">Loading users…</td>
        </tr>

        <!-- Error -->
        <tr *ngIf="!loading && errorMessage" class="empty-row">
          <td colspan="6" role="alert">
            {{ errorMessage }}
            <button type="button" class="link-btn" (click)="loadUsers()">Try again</button>
          </td>
        </tr>

        <!-- Rows -->
        <tr *ngFor="let user of visibleUsers; trackBy: trackById" [class.is-selected]="isSelected(user)">
          <td class="col-select">
            <input type="checkbox" [attr.aria-label]="'Select ' + fullName(user)"
                   [checked]="isSelected(user)" [disabled]="assigning"
                   (change)="toggleUser(user)" />
          </td>

          <td>
            <span class="user__primary" [title]="fullName(user)">{{ fullName(user) }}</span>
            <span class="user__secondary" [title]="user.email">{{ user.email }}</span>
          </td>

          <td class="cell-ellipsis" [title]="user.userName">{{ user.userName }}</td>

          <td class="cell-code">{{ user.employeeCode || '—' }}</td>

          <td>
            <span class="chip" [class.chip--success]="user.isActive" [class.chip--muted]="!user.isActive">
              <span class="chip__dot" aria-hidden="true"></span>
              {{ user.isActive ? 'Active' : 'Inactive' }}
            </span>
          </td>

          <td class="cell-actions">
            <button type="button" class="edit-btn" [attr.aria-label]="'Edit user ' + fullName(user)" (click)="onEdit(user)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
          </td>
        </tr>

        <!-- Empty -->
        <tr *ngIf="!loading && !errorMessage && visibleUsers.length === 0" class="empty-row">
          <td colspan="6">No users match your search.</td>
        </tr>
      </tbody>
    </table>
  </div>
</section>